import { Routes } from "@angular/router";
import { PublicLayoutComponent } from "./layouts/public-layout/public-layout.component";
import { PrivateLayoutComponent } from "./layouts/private-layout/private-layout.component";
import { LoginComponent } from "./components/login/login.component";
import { ForgotPasswordComponent } from "./components/forgot-password/forgot-password.component";
import { ResetPasswordComponent } from "./components/reset-password/reset-password.component";
import { PageNotFoundComponent } from "./components/page-not-found/page-not-found.component";
import { ServerErrorComponent } from "./components/server-error/server-error.component";
import { AuthGuard } from "./guards/auth.guard";
import { LoginGuard } from "./guards/login.guard";
import { SupportComponent } from "./components/support/support.component";
import { SettingsComponent } from "./components/settings/settings.component";
import { DynamicFormPocComponent } from "./components/dynamic-form-poc/dynamic-form-poc.component";
import { PermissionConstants } from "./constants/permission-constants";
import { dashboardUnsavedChangesGuard } from "./components/dashboard/dashboard-unsaved-changes.guard";
import { dashboardPermissionGuard, homeGuard } from "./components/dashboard/dashboard-home/home.guard";

const loadDashboardPage = () => import("./components/dashboard/dashboard.component").then(m => m.DashboardComponent);
/**
 * Home needs NAV_HOME, the dashboard pages NAV_DASHBOARD, create CREATE_DASHBOARD and edit EDIT_DASHBOARD;
 * `navUrl` = the sidebar item each page highlights.
 */
const HOME_PERMISSION = { permissionIds: [PermissionConstants.HOME.NAV] };
const DASHBOARD_PERMISSION = { permissionIds: [PermissionConstants.DASHBOARD.NAV] };
const DASHBOARD_CREATE_PERMISSION = { permissionIds: [PermissionConstants.DASHBOARD.CREATE] };
const DASHBOARD_EDIT_PERMISSION = { permissionIds: [PermissionConstants.DASHBOARD.EDIT] };

export const routes: Routes = [
  { path: "", redirectTo: "/login", pathMatch: "full" },
  {
    path: "login",
    component: PublicLayoutComponent,
    children: [{ path: "", component: LoginComponent }],
    canActivate: [LoginGuard]
  },
  {
    path: "forgot-password",
    component: PublicLayoutComponent,
    children: [{ path: "", component: ForgotPasswordComponent }],
    canActivate: [LoginGuard]
  },
  {
    path: "reset-password/:token",
    component: PublicLayoutComponent,
    children: [{ path: "", component: ResetPasswordComponent }],
    canActivate: [LoginGuard]
  },
  {
    path: "container-receiving",
    component: PublicLayoutComponent,
    children: [{ path: "", loadComponent: ()=>import('./components/container-receiving/container-receiving.component').then(m => m.ContainerReceivingComponent) }],
    canActivate: []
  },
  {
    // Home: the user's preferred dashboard in place. Without NAV_HOME, homeGuard sends the user to the first page
    // their menu allows (or the layout shows No access).
    path: "home",
    component: PrivateLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: "",
        canActivate: [homeGuard],
        data: { requiresPermission: HOME_PERMISSION, navUrl: "/home" },
        loadComponent: () => import("./components/dashboard/dashboard-home/dashboard-home.component").then(m => m.DashboardHomeComponent)
      }
    ]
  },
  {
    // Manage dashboards (the list) + the dashboard pages; all highlight "Dashboards"
    path: "dashboards",
    component: PrivateLayoutComponent,
    canActivate: [AuthGuard],
    data: { requiresPermission: DASHBOARD_PERMISSION, navUrl: "/dashboards" },
    children: [
      {
        path: "",
        loadComponent: () => import("./components/dashboard/dashboard-list/dashboard-list.component").then(m => m.DashboardListComponent)
      },
      {
        path: "create",
        loadComponent: loadDashboardPage,
        canActivate: [dashboardPermissionGuard],
        data: { dashboardMode: "create", showSideNav: false, requiresPermission: DASHBOARD_CREATE_PERMISSION },
        canDeactivate: [dashboardUnsavedChangesGuard]
      },
      {
        path: "view/:id",
        loadComponent: loadDashboardPage,
        data: { dashboardMode: "view" },
        canDeactivate: [dashboardUnsavedChangesGuard]
      },
      {
        path: "edit/:id",
        loadComponent: loadDashboardPage,
        canActivate: [dashboardPermissionGuard],
        data: { dashboardMode: "edit", showSideNav: false, requiresPermission: DASHBOARD_EDIT_PERMISSION },
        canDeactivate: [dashboardUnsavedChangesGuard]
      }
    ]
  },
  // old dashboard URLs (bookmarks, links in mails)
  { path: "dashboard", pathMatch: "full", redirectTo: "/home" },
  { path: "dashboard/manage", redirectTo: "/dashboards" },
  { path: "dashboard/create", redirectTo: "/dashboards/create" },
  { path: "dashboard/view/:id", redirectTo: "/dashboards/view/:id" },
  { path: "dashboard/edit/:id", redirectTo: "/dashboards/edit/:id" },
  {
    path: "admin",
    loadChildren: () => import('./modules/admin/admin.module').then(m => m.AdminModule),
    canActivate: [AuthGuard]
  },
  {
    path: "manage-masters",
    loadChildren: () => import('./modules/masters/masters.module').then(m => m.MastersModule),
    canActivate: [AuthGuard]
  },

  {
    path: "manage-finance",
    loadChildren: () => import('./modules/finance/finance.module').then(m => m.FinanceModule),
    canActivate: [AuthGuard]
  },

  {
    path: "manage-operations",
    loadChildren: () => import('./modules/operations/operations.module').then(m => m.OperationsModule),
    canActivate: [AuthGuard]
  },
  {
    path: "documentation",
    loadChildren: () => import('./modules/documentation/documentation.module').then(m => m.DocumentationModule),
    canActivate: [AuthGuard]
  },
  {
    path: "manage-inventory",
    loadChildren: () => import('./modules/inventory/inventory.module').then(m => m.InventoryModule),
    canActivate: [AuthGuard]
  },
  {
    path: "reports",
    loadChildren: () => import('./modules/reports/reports.module').then(m => m.ReportsModule),
    canActivate: [AuthGuard]
  },
  {
    path: "customer-portal",
    loadChildren: () => import('./modules/customerPortal/customerPortal.module').then(m => m.CustomerPortalModule),
    canActivate: [AuthGuard]
  },
  /*    {
        path: "test/side-panel",
        component: PrivateLayoutComponent,
        children: [{ path: "", component: ChipInputComponent }],
        canActivate: [AuthGuard]
      },
      {
        path: "test/payment",
        component: PrivateLayoutComponent,
        children: [{ path: "", component: RecordPaymentComponent }],
        canActivate: [AuthGuard]
      },*/
  {
    path: "test/dynamic-form",
    component: PrivateLayoutComponent,
    children: [{ path: "", component: DynamicFormPocComponent }],
    canActivate: [AuthGuard]
  },
  {
    path: "support",
    component: PrivateLayoutComponent,
    children: [{ path: "", component: SupportComponent }],
    canActivate: [AuthGuard]
  },
  {
    path: "settings",
    component: PrivateLayoutComponent,
    children: [{ path: "", component: SettingsComponent }],
    canActivate: [AuthGuard]
  },
  {
    path: "user-profile",
    component: PrivateLayoutComponent,
    children: [{ path: "", loadComponent: ()=>import('./components/user-profile/user-profile.component').then(m => m.UserProfileComponent) }],
    canActivate: [AuthGuard]
  },

  // Smart TOS couldn't serve a request (?returnUrl=/path for Try again); no guard: it must render signed in or out
  { path: "server-error", component: ServerErrorComponent },
  // Wildcard route for a 404 page
  { path: "**", component: PageNotFoundComponent }
];
